import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { Button } from 'src/components/ui/button';
import React, { useEffect, useState } from 'react';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { useAppSelector } from 'src/store';
import { ChevronDown } from 'lucide-react';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { useLazyGetAccountsQuery } from 'src/services/accountsService';

export const CompanySelectModal = ({
  disabled,
  accounts,
  changeAccountHandler,
  storageSelectedAccount,
}) => {
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState('Reset');
  const { managedAccountId } = useAppSelector((s) => s.companySlices);
  const { data: userInfo } = useGetUsersMeQuery();
  const [fetchAccountQuery, { data: accountData, isFetching, isError, error }] =
    useLazyGetAccountsQuery();

  useEffect(() => {
    if (userInfo) {
      fetchAccountQuery({ account: userInfo.account });
    }
  }, [userInfo]);

  useEffect(() => {
    if (accountData) {
      setCompanyName(accountData.company_name);
    }
  }, [accountData]);

  const {
    setValue,
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      managedAccountId: managedAccountId,
    },
    resolver: yupResolver(
      object({
        managedAccountId: stringScheme({ required: false }).label('Company Name'),
      }),
    ),
  });
  const selectedAccountId = watch('managedAccountId');

  const onOpenChange = (open) => {
    setOpen(open);

    if (open) {
      setValue('managedAccountId', String(managedAccountId));
    }
  };

  const submit = async (data) => {
    setOpen(false);
    changeAccountHandler(data.managedAccountId);
  };

  const disableCondition = Number(selectedAccountId) === Number(managedAccountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <div className="group/select-company flex max-w-[170px] max-h-[34px] h-screen items-center gap-1.5 py-[5px] px-3.5 border rounded-sm-5 border-ebony transition border-opacity-10 [&_p]:hover:opacity-100 hover:border-opacity-100 [&_svg]:hover:opacity-100">
          <p
            title={storageSelectedAccount?.company_name}
            className="text-ebony font-medium opacity-80 text-base-xs transition max-w-40 overflow-hidden overflow-ellipsis text-nowrap"
          >
            {storageSelectedAccount ? storageSelectedAccount?.company_name : 'Select company'}
          </p>
          <ChevronDown
            color={'#05050D'}
            className="min-w-[18px] w-3 opacity-80 group/select-company-hover:opacity-100"
          />
        </div>
      </DialogTrigger>
      <DialogContent className={'overflow-hidden'}>
        <DialogHeader>
          <DialogTitle>Choose a company</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4 max-h-[60vh] px-4 overflow-auto">
          <form onSubmit={handleSubmit(submit)} id="company-form">
            <Controller
              control={control}
              name="managedAccountId"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={onChange}
                  defaultValue={value}
                  className="flex flex-col space-y-1"
                >
                  {accounts.map((account) => (
                    <div key={account.id} className="flex items-center space-x-5 space-y-0">
                      <RadioGroupItem
                        value={`${account.account.id}`}
                        id={`${account.account.company_name}-${account.account.id}`}
                      />
                      <label
                        htmlFor={`${account.account.company_name}-${account.account.id}`}
                        className="cursor-pointer"
                      >
                        <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                          {account.account.company_name}
                        </p>
                      </label>
                    </div>
                  ))}
                  <div
                    className={clsx(
                      'flex items-center space-x-5 space-y-0',
                      !managedAccountId && 'pointer-events-none opacity-40',
                    )}
                  >
                    <RadioGroupItem value={''} id="reset" />
                    <label htmlFor="reset" className="cursor-pointer">
                      <p className="font-medium text-base text-ebony opacity-70 hover:opacity-100 transition">
                        {companyName}
                      </p>
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
          </form>
        </div>
        <DialogFooter className={'md:justify-center'}>
          <DialogClose asChild>
            <Button variant={'outline'} className="transparent black" style={{ width: 'auto' }}>
              Cancel
            </Button>
          </DialogClose>
          <DialogClose asChild autoFocus type="submit" form="company-form">
            <Button disabled={disableCondition} className="green" style={{ width: 'auto' }}>
              Choose
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
